import React, {
  createContext,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Switch, Route, Redirect, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import routes, { routesWithUserSystem } from "../utils/routes.js";
import styles from "../components/assets/jss/material-dashboard-react/layouts/adminStyle.js";
import { auth, firestore } from "../utils/firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { useReactToPrint } from "react-to-print";
import AutoPrint from "../pages/AutoPrint.jsx";
import Audio from "../components/assets/notification.mp3";
import { toast } from "react-toastify";
import { FiArrowUpRight, FiGrid, FiPackage, FiPhone } from "react-icons/fi";
import "../css/order-notifications.css";
import { isMainPanelRestricted } from "../utils/panelAccess";
var _info = localStorage.getItem("info");

if (_info == "undefined") {
  localStorage.clear("info");
  _info = null;
}
const info = JSON.parse(_info);


const useStyles = makeStyles(styles);
export const AdminContext = createContext();

const formatOrderTotal = (order) => {
  if (order?.arama || order?.paket) return Number(order.total) || 0;

  return (order?.cart || []).reduce((total, item) => {
    const itemTotal = Number(item.total) || 0;
    const optionTotal = Number(item.optPrice) || 0;
    const quantity = Number(item.paid) || Number(item.quantity) || 0;
    return total + (itemTotal + optionTotal) * quantity;
  }, 0);
};

const getOrderItemsSummary = (order, menuObj) => {
  const items = (order?.cart || []).map((item) => {
    const name = menuObj?.[item.meal]?.name || item.name || "Ürün";
    const quantity = Number(item.paid) || Number(item.quantity) || 1;
    return `${quantity}× ${name}`;
  });

  if (items.length <= 2) return items.join(" · ") || "Sipariş içeriği hazır";
  return `${items.slice(0, 2).join(" · ")} +${items.length - 2} ürün`;
};

// Firestore her snapshot'ta tüm dokümanları döndürür. Sadece değişen kaydı
// güncelleyerek, değişmemiş siparişlerin referansını koruyoruz. Böylece başka
// masadaki bir değişiklik seçili masanın adisyonunu yeniden çizdirmiyor.
const applyOrderChanges = (previousOrders, snapshot) => {
  const previousById = new Map(previousOrders.map((order) => [order.id, order]));
  let changed = previousOrders.length !== snapshot.size;

  snapshot.docChanges().forEach((change) => {
    const id = change.doc.id;
    if (change.type === "removed") {
      if (previousById.delete(id)) changed = true;
      return;
    }

    const nextOrder = change.doc.data();
    if (previousById.get(id) !== nextOrder) {
      previousById.set(id, nextOrder);
      changed = true;
    }
  });

  if (!changed) return previousOrders;
  return snapshot.docs.map((doc) => previousById.get(doc.id) || doc.data());
};

function OrderNotification({ order, type, tableName, menuObj, onOpen }) {
  const isPackage = type === "package";
  const isPhone = type === "phone";
  const Icon = isPackage ? FiPackage : isPhone ? FiPhone : FiGrid;
  const title = isPackage
    ? order.name || "Yeni paket siparişi"
    : isPhone
      ? order.name || order.phone || "Yeni telefon siparişi"
      : tableName || "Yeni masa siparişi";
  const subtitle = isPackage ? "Paket siparişi" : isPhone ? "Telefon siparişi" : "Masa siparişi";

  return (
    <div className={`order-notification order-notification--${type}`} role="article">
      <div className="order-notification__icon" aria-hidden="true"><Icon size={21} /></div>
      <div className="order-notification__content">
        <div className="order-notification__header">
          <div className="order-notification__topline">
            <span className="order-notification__badge"><span aria-hidden="true" /> Yeni sipariş</span>
            <strong>{subtitle}</strong>
          </div>
          <h3>{title}</h3>
        </div>
        <p>{getOrderItemsSummary(order, menuObj)}</p>
        <div className="order-notification__bottomline">
          <div className="order-notification__amount">
            <span>Toplam tutar</span>
            <strong>{formatOrderTotal(order).toFixed(2)} TL</strong>
          </div>
          <button type="button" onClick={onOpen} aria-label={`${title} siparişini aç`}>
            <span>Siparişi aç</span> <FiArrowUpRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}


export default function Admin({ ...rest }) {
  // styles
  const classes = useStyles();
  const mainPanel = useRef(null);
  const [cafe, setCafe] = useState("");
  const history = useHistory();
  const [user, loading, error] = useAuthState(auth);
  const [orders, setOrders] = useState([]);
  const [paketOrders, setPaketOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [tablesObj, setTablesObj] = useState([]);
  const [menu, setMenu] = useState(null);
  const [menuObj, setMenuObj] = useState(null);
  const [restaurantInfo, setRestaurantInfo] = useState(info || {});
  const isMainPanel =
    restaurantInfo?.multiPanel !== true ? true :
      (localStorage.getItem("mainPanel") == "Y");
  const mainPanelRestricted = isMainPanelRestricted(restaurantInfo);

  useEffect(() => {
    if (!loading && !user) {
      history.push("/login");
    }
  }, [loading, user, history]);

  const goToAdmin = () => {
    history.push("/adminpanel");
  };
  const getLastOrder = () => {
    let order = orders.sort((a, b) => {
      if (a.time < b.time) return -1;
      else return 1;
    })[0];
    return order;
  };

  const showOrderNotification = (order, type, tableName, notificationMenuObj = menuObj) => {
    toast(
      <OrderNotification
        order={order}
        type={type}
        tableName={tableName}
        menuObj={notificationMenuObj}
        onOpen={() => {
          toast.dismiss();
          history.push(type === "table" ? "/admin/masalar" : "/admin/siparisler");
        }}
      />,
      {
        position: "bottom-right",
        autoClose: false,
        closeOnClick: true,
        closeButton: true,
        className: "order-toast",
        pauseOnHover: true,
        draggable: false,
      }
    );
  };


  // Tek bir Provider, tüm rotaların dışında. Değeri memoize edilmiyorsa her render'da
  // yeni obje üretir ve alt ağaçtaki hiçbir React.memo tutmaz.
  const contextValue = useMemo(
    () => ({
      orders,
      paketOrders,
      tables,
      tablesObj,
      menu,
      menuObj,
      isMainPanel,
      mainPanelRestricted,
    }),
    [
      orders,
      paketOrders,
      tables,
      tablesObj,
      menu,
      menuObj,
      isMainPanel,
      mainPanelRestricted,
    ]
  );

  const activeRoutes = restaurantInfo?.hasUserSystem
    ? routesWithUserSystem
    : routes;

  const switchRoutes = useMemo(
    () => (
      <Switch>
        {activeRoutes?.map((prop, key) => {
          if (prop.layout !== "/admin") return null;
          return (
            <Route
              path={prop.layout + prop.path}
              render={() =>
                mainPanelRestricted && !prop.tabletAllowed ? (
                  <Redirect to="/admin/masalar" />
                ) : (
                  <prop.component />
                )
              }
              key={key}
            />
          );
        })}
        <Redirect from="/admin" to="/admin/dashboard" />
      </Switch>
    ),
    [activeRoutes, mainPanelRestricted]
  );
  const printRef = useRef();
  const [orderToPrint, setOrderToPrint] = useState(null);
  const printData = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => setOrderToPrint(null),
  });

  useEffect(() => {
    if (orderToPrint && restaurantInfo?.autoPrint) printData();
  }, [orderToPrint]);
  useEffect(() => {
    let name;
    // Unmount sonrası çözülen .then zincirlerinin yeni dinleyici açmasını engeller.
    let cancelled = false;
    const unsubscribers = [];
    const track = (unsubscribe) => {
      if (cancelled) unsubscribe();
      else unsubscribers.push(unsubscribe);
    };

    // Açık siparişleri dünün başlangıcından itibaren dinle: gün boyu açık kalan
    // panelde sonuç setinin ve yerel önbelleğin sınırsız büyümesini engeller.
    const ordersSince = new Date();
    ordersSince.setHours(0, 0, 0, 0);
    ordersSince.setDate(ordersSince.getDate() - 1);

    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (user && !cancelled) {
        firestore
          .collection("Admin")
          .doc(user.email)
          .get()
          .then((doc) => {
            if (cancelled) return;
            name = doc.data().restaurant;

            setCafe(name);
            localStorage.setItem("name", doc.data().restaurant);
            if (doc.data().type == "Admin") goToAdmin();
          })
          .then(() => {
            if (cancelled) return;
            let ref = firestore.collection(`Restaurants/${name}/info`);
            ref
              .doc("tables")
              .get()
              .then((doc) => {
                if (cancelled) return;
                if (doc.exists) {
                  let masa = Object.entries(doc.data()).sort((a, b) =>
                    parseInt(a[1].sortOrder) > parseInt(b[1].sortOrder) ? 1 : -1
                  );
                  setTables(masa);
                  let tables = Object.assign({}, doc.data());
                  setTablesObj(tables);
                  return tables;
                } else {
                  // doc.data() will be undefined in this case
                  console.log("No such document!");
                }
              })
              .then((tables) => {
                if (cancelled) return;
                var MenuOBJ;
                // Menü içeriği gerçekten değişmediyse state'i tazelemiyoruz; aksi halde
                // Restaurants dokümanına yapılan her yazım tüm POS ağacını render ediyor.
                let menuSignature = null;
                track(
                  firestore
                    .collection(`Restaurants`)
                    .doc(name)
                    .onSnapshot((doc) => {
                      let getMenu = doc.data().Menu;
                      getMenu.sort((a, b) => {
                        if (a.sortOrder < b.sortOrder) return -1;
                        else return 1;
                      });
                      getMenu = getMenu.filter((m) => m.active == 1);
                      MenuOBJ = Object.assign({}, ...(getMenu.map(m => m.items.map(item => ({ [item.id]: item }))).flat()));

                      const nextSignature = JSON.stringify(getMenu);
                      if (nextSignature === menuSignature) return;
                      menuSignature = nextSignature;
                      setMenu(getMenu);
                      setMenuObj(MenuOBJ);
                    })
                );

                if (isMainPanel) {
                  track(
                    firestore
                      .collection(`Restaurants/${name}/requests`)
                      .where("seen", "==", false)
                      .onSnapshot(doc => {
                        doc.docChanges().forEach((change) => {
                          if (change.type === "added") {
                            firestore
                              .collection(`Restaurants/${name}/requests`).doc(change.doc.id).update({ seen: true });
                            let newRequest = change.doc.data();
                            document.getElementsByClassName("audio-element")[0].play();
                            toast.success(tables[newRequest.table].name + ": " + newRequest.request + " İsteği", {
                              position: "bottom-center",
                              autoClose: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                            });
                          }
                        })
                      })
                  );
                }

                const handleOrdersSnapshot = (doc) => {
                  setOrders((previousOrders) =>
                    applyOrderChanges(previousOrders, doc)
                  );

                  doc.docChanges().forEach((change) => {
                    if (change.type === "added") {
                      try {
                        if (change.doc.data().seen) return;

                        let newOrder = change.doc.data();
                        if (isMainPanel) {
                          var count = newOrder.cart.filter(x => MenuOBJ[x.meal].noPrint == null || MenuOBJ[x.meal].noPrint == false).length;
                          if (count > 0) {
                            setOrderToPrint(newOrder);
                            document.getElementsByClassName("audio-element")[0].play();
                          }
                          firestore
                            .collection(`Restaurants/${name}/orders`).doc(change.doc.id).set({ seen: true }, { merge: true });

                          if (newOrder.noNotification || restaurantInfo.noNotification) return;
                          showOrderNotification(newOrder, "table", tables[newOrder.table]?.name, MenuOBJ);
                        }
                      } catch (err) {
                        console.error(err);
                      }
                    }
                  });
                };

                const subscribeOrders = (scoped) => {
                  let query = firestore
                    .collection(`Restaurants/${name}/orders`)
                    .where("status", "==", 0);
                  if (scoped) query = query.where("date", ">=", ordersSince);
                  return query.onSnapshot(handleOrdersSnapshot, (error) => {
                    // (status ASC, date ASC) bileşik indeksi yoksa Firestore
                    // failed-precondition döner; panel boş kalmasın diye tarih
                    // filtresiz sorguya düşüyoruz.
                    if (scoped && error?.code === "failed-precondition") {
                      console.warn(
                        "orders bileşik indeksi bulunamadı, tarih filtresi olmadan dinleniyor:",
                        error.message
                      );
                      track(subscribeOrders(false));
                      return;
                    }
                    console.error(error);
                  });
                };

                track(subscribeOrders(true));

                if (isMainPanel) {
                  track(
                    firestore
                      .collection(`Restaurants/${name}/paket-orders`)
                      .where("status", "in", [0, 1])
                      .onSnapshot((doc) => {

                      setPaketOrders(doc.docs.map((d) => d.data()));

                      doc.docChanges().forEach((change) => {
                        if (change.type === "added") {
                          try {
                            if (change.doc.data().seen) return;
                            firestore
                              .collection(`Restaurants/${name}/paket-orders`).doc(change.doc.id).set({ seen: true }, { merge: true });
                            let newOrder = change.doc.data();
                            if (isMainPanel) {
                              setOrderToPrint(newOrder); document.getElementsByClassName("audio-element")[0].play();
                            }

                            if (newOrder.noNotification) return;


                            showOrderNotification(
                              newOrder,
                              newOrder.paket ? "package" : "phone",
                              tables[newOrder.table]?.name,
                              MenuOBJ
                            );

                          } catch (err) {
                            console.error(err);
                          }
                        }
                      });
                      })
                  );
                }
              });
            firestore
              .collection(`Restaurants/${name}/info`)
              .doc("info")
              .get()
              .then((doc) => {
                if (cancelled) return;
                if (doc.exists) {
                  const nextInfo = doc.data();
                  localStorage.setItem("info", JSON.stringify(nextInfo));
                  setRestaurantInfo(nextInfo);
                } else {
                  // doc.data() will be undefined in this case
                  console.log("No such document!");
                }
              });
          });
      }
    });

    return () => {
      cancelled = true;
      unsubAuth();
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      unsubscribers.length = 0;
    };
  }, []);

  return (
    user && (
      <div className={classes.wrapper}>
        {/*<Sidebar
          routes={info?.hasUserSystem ? dashboardRoutes1 : routes}
          logoText={cafe}
          logo={logo}
          image={image}
          handleDrawerToggle={handleDrawerToggle}
          open={mobileOpen}
          color={color}
          {...rest}
        />*/}
        {isMainPanel && (
          <div style={{ display: "none" }}>
            <AutoPrint
              price={false}
              ref={printRef}
              order={orderToPrint}
              table={tablesObj[orderToPrint?.table]}
              menuObj={menuObj}
              tablesObj={tablesObj}
            />
          </div>
        )}
        <div className={classes.mainPanel} ref={mainPanel}>
          {/* On the /maps route we want the map to be on full screen -
         this is not possible if the content and conatiner classes are present 
         because they have some paddings which would make the map smaller */}
          <Suspense fallback={<div className="panel-loading">Yükleniyor...</div>}>
            <AdminContext.Provider value={contextValue}>
              {switchRoutes}
            </AdminContext.Provider>
          </Suspense>
        </div>
        {isMainPanel && (
          <audio className="audio-element">
            <source src={Audio}></source>
          </audio>
        )}
      </div>
    )
  );
}
